import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserService } from 'shared/domain';
import { toHttpParams, TreetopPropertiesProvider } from 'shared/util-common';

import { Taxonomy, TaxonomyNode } from '@treetop/shared/model';

export type TaxonomyFilter = {
  organisationId: string;
};

export type GetTaxonomyOptions = {
  filter: TaxonomyFilter;
};

@Injectable({ providedIn: 'root' })
export class TaxonomyHttpService {
  private userService = inject(UserService, { optional: true });

  private http = inject(HttpClient);
  protected baseUrl = `${TreetopPropertiesProvider.properties.baseUrl}api`;

  private mapTaxonomy = (t: Taxonomy): TaxonomyNode => ({
    id: t.id,
    title: t.title,
    parentId: t.parentId,
    children: t.children?.map(this.mapTaxonomy) ?? [],
    item: { organisationId: t.organisationId },
  });

  public getTaxonomy(options?: GetTaxonomyOptions): Observable<TaxonomyNode[]> {
    const params = toHttpParams({ organisationId: options?.filter?.organisationId });

    return this.http.get<Taxonomy[]>(`${this.baseUrl}/taxonomies`, { params }).pipe(map((nodes) => nodes.map(this.mapTaxonomy)));
  }

  public addNode(node: TaxonomyNode): Observable<Taxonomy[]> {
    return this.http.post<Taxonomy[]>(`${this.baseUrl}/taxonomies`, {
      id: node.id,
      title: node.title,
      parentId: node.parentId,
      order: node.order,
      organisationId: this.userService?.user().currentOrganisation.id,
    });
  }

  public editNode(node: TaxonomyNode): Observable<Taxonomy[]> {
    return this.http.put<Taxonomy[]>(`${this.baseUrl}/taxonomies`, {
      id: node.id,
      title: node.title,
      parentId: node.parentId,
      order: node.order,
      organisationId: this.userService?.user().currentOrganisation.id,
    });
  }

  public deleteNode(node: TaxonomyNode): Observable<Taxonomy[]> {
    return this.http.delete<Taxonomy[]>(`${this.baseUrl}/taxonomies/${node.id}`);
  }
}
